import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, NgZone, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetslipBarConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { CalculatedOdds, OddsOperations } from '@frontend/sports/odds/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { BetBuilderConfigService } from 'packages/sports/web/app/src/bet-builder/services/bet-builder-config.service';
import { Observable, combineLatest, debounceTime, distinctUntilKeyChanged, filter, first, map, merge, pairwise, switchMap } from 'rxjs';

import {
    BetslipReturnNotification,
    BetslipReturnsMessagesNotificationService,
} from '../../../digital/services/betslip-returns-messages-notification.service';
import { NotificationType } from '../../acca-boost/model';
import { AccaBoostNotificationSyncService } from '../../acca-boost/services/digital/acca-boost-notification-sync.service';
import { BetBarErrorTracking, BetslipBarDisplayMode, BetslipBarTrackingService } from '../services/betslip-bar-tracking.service';
import { BetslipBarVisibilityService } from '../services/betslip-bar-visibility.service';
import { BetslipBarModel, BetslipBarService, BetslipBarTranslations } from '../services/betslip-bar.service';

interface BetslipBarViewModel extends BetslipBarModel {
    visible: boolean;
    oddsDisplayFormat: OddsDisplayFormat;
    displayMode: BetslipBarDisplayMode;
    accaBoostNotification: BetslipReturnNotification;
}

@Component({
    selector: 'bs-betslip-bar',
    templateUrl: './betslip-bar.component.html',
    styleUrls: ['./betslip-bar.component.scss'],
    providers: [AccaBoostNotificationSyncService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BetslipBarComponent {
    private readonly model$ = this.betslipBarService.model$;
    private readonly visible$ = this.betslipBarVisibility.componentVisible$;
    private readonly oddsDisplayFormat$ = this.betslipBarService.oddsDisplayFormat$;
    private readonly notifications$ = this.notificationService.notifications$.pipe(debounceTime(100));

    translations: BetslipBarTranslations & { MyBets: string };

    @Output() barClick = new EventEmitter<void>();
    @Output() myBetsClick = new EventEmitter<void>();

    vm$: Observable<BetslipBarViewModel> = combineLatest([this.model$, this.visible$, this.oddsDisplayFormat$, this.notifications$]).pipe(
        map(([model, visible, oddsDisplayFormat, notification]) => ({
            ...model,
            visible,
            oddsDisplayFormat,
            displayMode: this.trackingService.componentDisplayMode(
                this.myBetsShown,
                !!model.showUndo,
                model.pickCount,
                !!model.betBuilderBadge,
                model.isMulti,
            ),
            accaBoostNotification: notification,
        })),
    );

    @HostBinding('class') className = 'betslip-bar fixed-bottom betslip-vars-scope';

    @HostBinding('class.epcot-enabled') epcotEnabled = this.epcotConfig.isEnabled;

    notificationTypeEnum = NotificationType;

    private ngZone = inject(NgZone);

    constructor(
        private betslipBarService: BetslipBarService,
        private betslipBarVisibility: BetslipBarVisibilityService,
        private trackingService: BetslipBarTrackingService,
        private betBuilderConfigService: BetBuilderConfigService,
        private strategy: RxStrategyProvider,
        public config: BetslipBarConfig,
        sitecore: Sitecore,
        private epcotConfig: EpcotConfig,
        private notificationService: BetslipReturnsMessagesNotificationService,
    ) {
        this.translations = {
            ...sitecore.betslip.betslipBar,
            MyBets: sitecore.lobby.MyBets,
        };
        this.vm$.pipe(first(), takeUntilDestroyed()).subscribe(({ displayMode }) => this.trackingService.trackLoad(displayMode));

        merge(
            //track the first occurance of any errors
            this.vm$.pipe(
                distinctUntilKeyChanged('modelError'),
                filter((model) => !!model.modelError),
            ),
            //we need to track UncombinableSelections and AvailabilityChanged further if user keeps adding picks
            this.betslipBarService.pickListCount$.pipe(
                pairwise(),
                filter(([previous, current]) => current > previous),
                switchMap(([_, currentPickListCount]) =>
                    this.vm$.pipe(
                        first(
                            (model) =>
                                model.comboBetPickCount === currentPickListCount &&
                                !!model.modelError &&
                                model.modelError !== BetBarErrorTracking.Undo,
                        ),
                    ),
                ),
            ),
        )
            .pipe(
                //debounce needed when both observable emit we dont want to track it twice
                //e.g. first occurance of UncombinableSelections
                debounceTime(5),
                takeUntilDestroyed(),
            )
            .subscribe((model) => {
                this.trackingService.trackLoad(model.displayMode, model.modelError);
            });
    }

    get myBetsShown(): boolean {
        return this.config.displayMyBets;
    }

    barClicked({ displayMode }: BetslipBarViewModel): void {
        this.strategy
            .schedule(
                () => {
                    this.barClick.next();
                    this.trackingService.trackClick('betslip', displayMode);
                },
                {
                    scope: this,
                    patchZone: this.ngZone,
                },
            )
            .subscribe();
    }

    myBetsClicked({ displayMode }: BetslipBarViewModel): void {
        this.strategy
            .schedule(
                () => {
                    this.myBetsClick.next();
                    this.trackingService.trackClick('my bets', displayMode);
                },
                {
                    scope: this,
                    patchZone: this.ngZone,
                },
            )
            .subscribe();
    }

    oddsVisible(odds: CalculatedOdds): boolean {
        return !OddsOperations.isOddsEmpty(odds);
    }

    removeLastLeg(event: Event, { displayMode }: BetslipBarViewModel) {
        event.stopPropagation();
        this.trackingService.trackClick('undo', displayMode);
        this.betslipBarService.removeLastLeg();
    }

    getBetBuilderBadge(model: BetslipBarViewModel): string {
        return model.betBuilderBadge + (model.isMulti ? '+' : '');
    }

    shouldShowNotification(): boolean {
        return this.betBuilderConfigService.showEmbeddedUi();
    }
}
